import { useEffect, useRef } from "react";
import type { CallStatus } from "../../voice/useLiveVoice";

// ---------------------------------------------------------------------------
// SiriOrb — the living sphere at the centre of the call.
//
// One canvas, one rAF loop, zero React re-renders: the loop reads the live mic
// / speaker levels straight from the audio graph (`levels()`) and paints.
//
// What it paints, back to front:
//   bloom          a soft halo that swells with whoever is talking
//   ripples        rings that break out of the sphere when the user speaks
//   silhouette     a circle deformed by three harmonics + a travelling wave,
//                  so the sphere is liquid rather than round
//   lobes          five orbiting pools of coloured light, additively blended
//                  inside the silhouette — the "fluid" of the orb
//   caustics       a slow rotating sweep, like light through water
//   glass          specular highlight, bottom inner shadow, glowing rim
//   sparks         orbiting motes that only appear while it is thinking
//
// Every colour is lerped between per-state palettes, so a state change reads
// as the liquid changing colour instead of a cut.
// ---------------------------------------------------------------------------

type RGB = [number, number, number];

interface Palette {
  bloom: RGB;
  core: RGB;
  lobes: [RGB, RGB, RGB, RGB, RGB];
  rim: RGB;
  spark: RGB;
}

const hex = (h: string): RGB => [
  parseInt(h.slice(1, 3), 16),
  parseInt(h.slice(3, 5), 16),
  parseInt(h.slice(5, 7), 16),
];

const PALETTES: Record<CallStatus, Palette> = {
  idle: {
    bloom: hex("#2e6b56"), core: hex("#08281d"), rim: hex("#c1e1d5"), spark: hex("#d6ebe3"),
    lobes: [hex("#46a580"), hex("#83c3aa"), hex("#5aaf8e"), hex("#c1e1d5"), hex("#2f7d5f")],
  },
  connecting: {
    bloom: hex("#2e6b56"), core: hex("#08281d"), rim: hex("#c1e1d5"), spark: hex("#d6ebe3"),
    lobes: [hex("#46a580"), hex("#83c3aa"), hex("#5aaf8e"), hex("#c1e1d5"), hex("#2f7d5f")],
  },
  listening: {
    bloom: hex("#6fb99c"), core: hex("#0e3d2c"), rim: hex("#eaf5f1"), spark: hex("#ffffff"),
    lobes: [hex("#a8e8cd"), hex("#5ccfa4"), hex("#2ea87f"), hex("#61d6c2"), hex("#8fe0bd")],
  },
  thinking: {
    bloom: hex("#5f9fd8"), core: hex("#0d3138"), rim: hex("#d6ebe3"), spark: hex("#bfe9ff"),
    lobes: [hex("#8e7dff"), hex("#3bd8ff"), hex("#56c79e"), hex("#b6dcff"), hex("#7a9dff")],
  },
  speaking: {
    bloom: hex("#46a580"), core: hex("#0b3526"), rim: hex("#eaf5f1"), spark: hex("#ffffff"),
    lobes: [hex("#6edaad"), hex("#28a774"), hex("#118a61"), hex("#46c5b4"), hex("#86dd9c")],
  },
  ended: {
    bloom: hex("#44534e"), core: hex("#111d19"), rim: hex("#cdd1d0"), spark: hex("#e6e8e7"),
    lobes: [hex("#9ba3a0"), hex("#697571"), hex("#828c88"), hex("#b4bab8"), hex("#505e59")],
  },
  error: {
    bloom: hex("#c25246"), core: hex("#260d10"), rim: hex("#ffd5c0"), spark: hex("#ffd5c0"),
    lobes: [hex("#ffd5c0"), hex("#ff953b"), hex("#e82c4e"), hex("#b3213c"), hex("#ff7a5c")],
  },
};

const MUTED_TINT: RGB = [123, 138, 132];

/** Per-state motion. `source` says which meter drives the reaction. */
const MOTION: Record<CallStatus, { swirl: number; wobble: number; breathe: number; source: "mic" | "out" | "none"; idleEnergy: number }> = {
  idle:       { swirl: 0.22, wobble: 0.8, breathe: 0.8, source: "none", idleEnergy: 0.04 },
  connecting: { swirl: 0.30, wobble: 0.9, breathe: 1.0, source: "none", idleEnergy: 0.10 },
  listening:  { swirl: 0.42, wobble: 1.0, breathe: 1.0, source: "mic",  idleEnergy: 0.05 },
  thinking:   { swirl: 1.25, wobble: 1.5, breathe: 1.7, source: "none", idleEnergy: 0.34 },
  speaking:   { swirl: 0.85, wobble: 1.4, breathe: 1.0, source: "out",  idleEnergy: 0.06 },
  ended:      { swirl: 0.12, wobble: 0.4, breathe: 0.5, source: "none", idleEnergy: 0.02 },
  error:      { swirl: 0.18, wobble: 0.6, breathe: 0.7, source: "none", idleEnergy: 0.05 },
};

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerpRGB = (a: RGB, b: RGB, t: number): RGB => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
const rgba = (c: RGB, a: number) => `rgba(${c[0] | 0}, ${c[1] | 0}, ${c[2] | 0}, ${a})`;

function lerpPalette(a: Palette, b: Palette, t: number): Palette {
  return {
    bloom: lerpRGB(a.bloom, b.bloom, t),
    core: lerpRGB(a.core, b.core, t),
    rim: lerpRGB(a.rim, b.rim, t),
    spark: lerpRGB(a.spark, b.spark, t),
    lobes: a.lobes.map((c, i) => lerpRGB(c, b.lobes[i], t)) as Palette["lobes"],
  };
}

interface Ripple { born: number; strength: number }

export interface SiriOrbProps {
  /** CSS pixel size of the square canvas (bloom included). */
  size: number;
  status: CallStatus;
  muted?: boolean;
  /** Live meters, read inside the loop — never through React state. */
  levels: () => { mic: number; out: number };
  /** Smoothed 0..1 energy, once per frame — drives the screen edge light. */
  onEnergy?: (energy: number) => void;
}

export default function SiriOrb({ size, status, muted = false, levels, onEnergy }: SiriOrbProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Refs so the loop never restarts (and never drops its animation phase).
  const statusRef = useRef<CallStatus>(status);
  const mutedRef = useRef(muted);
  const levelsRef = useRef(levels);
  const energyCbRef = useRef(onEnergy);
  // Latest-value refs, written after render so the loop below never restarts
  // (restarting would drop the animation phase and jump the orb).
  useEffect(() => {
    statusRef.current = status;
    mutedRef.current = muted;
    levelsRef.current = levels;
    energyCbRef.current = onEnergy;
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const cx = size / 2;
    const cy = size / 2;
    const R = size * 0.235;

    // Fixed random-ish constants — stable across frames so motion is coherent.
    const lobeSeed = [0.0, 1.27, 2.61, 3.92, 5.18];
    const lobeSpeed = [0.33, -0.27, 0.41, -0.19, 0.24];
    const lobeOrbit = [0.40, 0.52, 0.31, 0.46, 0.58];
    const sparks = Array.from({ length: 11 }, (_, i) => ({
      a: (i / 11) * Math.PI * 2,
      r: 1.16 + (i % 4) * 0.08,
      s: 0.22 + (i % 3) * 0.09,
      tw: i * 1.7,
      size: 1.1 + (i % 3) * 0.7,
    }));

    let raf = 0;
    let t = 0;
    let energy = 0;
    let swirlPhase = 0;
    let mix = 1;                       // palette cross-fade progress
    let fromPal = PALETTES[statusRef.current];
    let toStatus: CallStatus = statusRef.current;
    let muteMix = mutedRef.current ? 1 : 0;
    const ripples: Ripple[] = [];
    let lastRipple = 0;
    let last = performance.now();

    const draw = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      t += dt;

      const status = statusRef.current;
      const motion = MOTION[status] ?? MOTION.idle;

      // --- palette cross-fade ------------------------------------------
      if (status !== toStatus) {
        fromPal = lerpPalette(fromPal, PALETTES[toStatus], Math.min(mix, 1));
        toStatus = status;
        mix = 0;
      }
      mix = Math.min(1, mix + dt / 0.55);
      const eased = mix * mix * (3 - 2 * mix);
      let pal = lerpPalette(fromPal, PALETTES[toStatus], eased);
      muteMix += ((mutedRef.current ? 1 : 0) - muteMix) * Math.min(1, dt * 5);
      if (muteMix > 0.001) {
        pal = {
          bloom: lerpRGB(pal.bloom, MUTED_TINT, muteMix * 0.75),
          core: pal.core,
          rim: lerpRGB(pal.rim, MUTED_TINT, muteMix * 0.6),
          spark: lerpRGB(pal.spark, MUTED_TINT, muteMix * 0.6),
          lobes: pal.lobes.map((c) => lerpRGB(c, MUTED_TINT, muteMix * 0.7)) as Palette["lobes"],
        };
      }

      // --- energy ------------------------------------------------------
      const meters = levelsRef.current();
      const raw = motion.source === "mic" ? meters.mic : motion.source === "out" ? meters.out : 0;
      const driven = mutedRef.current && motion.source === "mic" ? 0 : raw;
      const target = Math.max(driven, motion.idleEnergy * (0.6 + 0.4 * Math.sin(t * (status === "thinking" ? 2.6 : 1.1))));
      // Fast attack, slow release — the orb punches on a syllable, settles softly.
      energy += (target - energy) * Math.min(1, dt * (target > energy ? 22 : 6));
      energy = Math.max(0, Math.min(1, energy));
      energyCbRef.current?.(energy);

      swirlPhase += dt * motion.swirl * (1 + energy * 1.6);

      // --- ripples -----------------------------------------------------
      const wantsRipple = status === "listening" && driven > 0.26 && now - lastRipple > 520;
      if (wantsRipple && !reduced && ripples.length < 3) {
        ripples.push({ born: now, strength: Math.min(1, driven * 1.5) });
        lastRipple = now;
      }
      while (ripples.length && now - ripples[0].born > 1800) ripples.shift();

      ctx.clearRect(0, 0, size, size);

      // --- bloom: light spilling into the room ------------------------
      const bloomR = R * (1.80 + energy * 0.20);
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      const bloom = ctx.createRadialGradient(cx, cy, R * 0.86, cx, cy, bloomR);
      bloom.addColorStop(0, rgba(pal.bloom, 0.46 + energy * 0.40));
      bloom.addColorStop(0.16, rgba(pal.bloom, 0.30 + energy * 0.30));
      bloom.addColorStop(0.42, rgba(pal.bloom, 0.12 + energy * 0.16));
      bloom.addColorStop(0.72, rgba(pal.bloom, 0.035 + energy * 0.05));
      bloom.addColorStop(1, rgba(pal.bloom, 0));
      ctx.fillStyle = bloom;
      ctx.beginPath();
      ctx.arc(cx, cy, bloomR, 0, Math.PI * 2);
      ctx.fill();

      // --- ripples: soft light, not wireframe --------------------------
      for (const rp of ripples) {
        const age = (now - rp.born) / 1800;
        if (age >= 1) continue;
        const ease = 1 - Math.pow(1 - age, 2.4);
        const rr = R * (1.02 + ease * 0.62);
        const fade = Math.pow(1 - age, 2) * rp.strength;
        const ring = ctx.createRadialGradient(cx, cy, rr * 0.72, cx, cy, rr * 1.26);
        ring.addColorStop(0, rgba(pal.bloom, 0));
        ring.addColorStop(0.5, rgba(pal.rim, 0.16 * fade));
        ring.addColorStop(1, rgba(pal.bloom, 0));
        ctx.fillStyle = ring;
        ctx.beginPath();
        ctx.arc(cx, cy, rr * 1.26, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();

      // --- liquid silhouette -------------------------------------------
      const breath = (reduced ? 0 : Math.sin(t * 0.9) * 0.022 * motion.breathe) + energy * 0.085;
      const amp = (0.006 + energy * 0.017) * motion.wobble * (reduced ? 0.35 : 1);
      const blob = new Path2D();
      const STEPS = 220;
      for (let i = 0; i <= STEPS; i++) {
        const a = (i / STEPS) * Math.PI * 2;
        let w =
          Math.sin(a * 2 + t * 0.7) * amp +
          Math.sin(a * 3 - t * 0.53 + 1.3) * amp * 0.72 +
          Math.sin(a * 5 + t * 0.91 + 2.7) * amp * 0.44;
        if (status === "speaking") w += Math.sin(a * 7 - t * 2.6) * energy * 0.011;
        if (status === "thinking") w += Math.sin(a * 4 + t * 1.9) * 0.007;
        const rr = R * (1 + breath + w);
        const x = cx + Math.cos(a) * rr;
        const y = cy + Math.sin(a) * rr;
        if (i === 0) blob.moveTo(x, y);
        else blob.lineTo(x, y);
      }
      blob.closePath();

      // --- inside the sphere -------------------------------------------
      const box = () => ctx.fillRect(cx - R * 1.5, cy - R * 1.5, R * 3, R * 3);
      const deep = lerpRGB(pal.core, [0, 0, 0], 0.35);
      ctx.save();
      ctx.clip(blob);

      // Body: lit from the upper left, falling to a saturated dark at the edge.
      const body = ctx.createRadialGradient(cx - R * 0.20, cy - R * 0.28, R * 0.04, cx, cy + R * 0.05, R * 1.06);
      body.addColorStop(0, rgba(lerpRGB(pal.core, pal.lobes[2], 0.72 + energy * 0.14), 1));
      body.addColorStop(0.42, rgba(lerpRGB(pal.core, pal.lobes[3], 0.40 + energy * 0.12), 1));
      body.addColorStop(0.8, rgba(pal.core, 1));
      body.addColorStop(1, rgba(deep, 1));
      ctx.fillStyle = body;
      box();

      // The fluid: pools of distinct hue drifting inside the glass.
      ctx.globalCompositeOperation = "lighter";
      for (let i = 0; i < 5; i++) {
        const ph = swirlPhase * lobeSpeed[i] * 2.4 + lobeSeed[i];
        const orbit = R * lobeOrbit[i] * (0.88 + energy * 0.34);
        const lx = cx + Math.cos(ph) * orbit;
        const ly = cy + Math.sin(ph * 1.13 + lobeSeed[i]) * orbit * 0.86;
        const lr = R * (0.54 + 0.12 * Math.sin(t * 0.8 + i * 1.7) + energy * 0.16);
        const alpha = 0.46 + energy * 0.24;
        const g = ctx.createRadialGradient(lx, ly, 0, lx, ly, lr);
        g.addColorStop(0, rgba(pal.lobes[i], alpha));
        g.addColorStop(0.4, rgba(pal.lobes[i], alpha * 0.40));
        g.addColorStop(1, rgba(pal.lobes[i], 0));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(lx, ly, lr, 0, Math.PI * 2);
        ctx.fill();
      }

      // Fresnel: light gathering just inside the shell — what makes it glass.
      const fres = ctx.createRadialGradient(cx, cy, R * 0.55, cx, cy, R * 1.04);
      fres.addColorStop(0, rgba(pal.rim, 0));
      fres.addColorStop(0.72, rgba(pal.rim, 0.05 + energy * 0.05));
      fres.addColorStop(0.94, rgba(pal.rim, 0.26 + energy * 0.20));
      fres.addColorStop(1, rgba(pal.rim, 0.05));
      ctx.fillStyle = fres;
      box();

      // Caustic sweep — light bending through the liquid.
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(swirlPhase * 0.55);
      ctx.scale(1, 0.30);
      const caustic = ctx.createRadialGradient(0, 0, 0, 0, 0, R * 1.0);
      caustic.addColorStop(0, rgba(pal.rim, 0.14 + energy * 0.14));
      caustic.addColorStop(0.55, rgba(pal.rim, 0.04));
      caustic.addColorStop(1, rgba(pal.rim, 0));
      ctx.fillStyle = caustic;
      ctx.beginPath();
      ctx.arc(0, 0, R * 1.0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Volume: the lower right falls into shadow.
      ctx.globalCompositeOperation = "source-over";
      const shade = ctx.createRadialGradient(cx + R * 0.34, cy + R * 0.52, R * 0.08, cx + R * 0.2, cy + R * 0.34, R * 1.15);
      shade.addColorStop(0, "rgba(0, 0, 0, 0.40)");
      shade.addColorStop(0.55, "rgba(0, 0, 0, 0.12)");
      shade.addColorStop(1, "rgba(0, 0, 0, 0)");
      ctx.fillStyle = shade;
      box();

      // A single sharp catch-light: the only truly white thing on the orb.
      const dotX = cx - R * 0.36;
      const dotY = cy - R * 0.46;
      ctx.globalCompositeOperation = "lighter";
      const sheen = ctx.createRadialGradient(dotX, dotY, 0, dotX, dotY, R * 0.52);
      sheen.addColorStop(0, "rgba(255, 255, 255, 0.11)");
      sheen.addColorStop(1, "rgba(255, 255, 255, 0)");
      ctx.fillStyle = sheen;
      box();
      const dot = ctx.createRadialGradient(dotX, dotY, 0, dotX, dotY, R * 0.115);
      dot.addColorStop(0, "rgba(255, 255, 255, 0.72)");
      dot.addColorStop(0.42, "rgba(255, 255, 255, 0.16)");
      dot.addColorStop(1, "rgba(255, 255, 255, 0)");
      ctx.fillStyle = dot;
      ctx.beginPath();
      ctx.arc(dotX, dotY, R * 0.115, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // --- rim: bright edge + iridescent arc -----------------------------
      ctx.save();
      const rimGrad = ctx.createLinearGradient(cx - R, cy - R, cx + R, cy + R);
      rimGrad.addColorStop(0, rgba(pal.rim, 0.34));
      rimGrad.addColorStop(0.4, rgba(pal.rim, 0.10));
      rimGrad.addColorStop(0.75, rgba(pal.rim, 0.60 + energy * 0.28));
      rimGrad.addColorStop(1, rgba(pal.rim, 0.18));
      ctx.strokeStyle = rimGrad;
      ctx.lineWidth = 1.25;
      ctx.shadowColor = rgba(pal.bloom, 0.9);
      ctx.shadowBlur = 20 + energy * 34;
      ctx.stroke(blob);
      ctx.shadowBlur = 0;
      // A whisper of iridescence along the lower-left edge.
      ctx.save();
      ctx.clip(blob);
      ctx.globalCompositeOperation = "lighter";
      const irid = ctx.createLinearGradient(cx - R, cy + R, cx + R * 0.4, cy - R * 0.6);
      irid.addColorStop(0, rgba(pal.spark, 0.16));
      irid.addColorStop(0.5, rgba(pal.spark, 0.04));
      irid.addColorStop(1, rgba(pal.spark, 0));
      ctx.strokeStyle = irid;
      ctx.lineWidth = 10;
      ctx.stroke(blob);
      ctx.restore();
      ctx.restore();

      // --- corona: light leaking past the shell ---------------------------
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      const coronaR = R * 1.34;
      const corona = ctx.createRadialGradient(cx, cy, R * 0.94, cx, cy, coronaR);
      corona.addColorStop(0, rgba(pal.rim, 0.20 + energy * 0.24));
      corona.addColorStop(0.35, rgba(pal.bloom, 0.10 + energy * 0.14));
      corona.addColorStop(1, rgba(pal.bloom, 0));
      ctx.fillStyle = corona;
      ctx.beginPath();
      ctx.arc(cx, cy, coronaR, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // --- thinking motes -------------------------------------------------
      if (status === "thinking" && !reduced) {
        ctx.save();
        ctx.globalCompositeOperation = "lighter";
        for (const s of sparks) {
          const a = s.a + t * s.s;
          const rr = R * s.r * (1 + Math.sin(t * 0.9 + s.tw) * 0.05);
          const x = cx + Math.cos(a) * rr;
          const y = cy + Math.sin(a) * rr * 0.92;
          const tw = 0.35 + 0.65 * Math.abs(Math.sin(t * 1.7 + s.tw));
          const g = ctx.createRadialGradient(x, y, 0, x, y, s.size * 5);
          g.addColorStop(0, rgba(pal.spark, 0.9 * tw));
          g.addColorStop(1, rgba(pal.spark, 0));
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(x, y, s.size * 5, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [size]);

  return <canvas ref={canvasRef} aria-hidden style={{ display: "block", willChange: "contents" }} />;
}
